import React, { useCallback, useState } from 'react';
import { useActor } from '@xstate/react';
import { useField } from 'informed';
import Timer from 'react-compound-timer/build';
import { useTranslation } from 'react-i18next';

import { API_FETCHING, API_SUCCESS } from '@nw/api-constants';
import {
  OTP_LOADING,
  RESEND_EMAIL_OTP_CLICKED,
  RESEND_OTP_CLICKED,
  VERIFY_EMAIL_OTP_BUTTON_CLICKED,
  VERIFY_OTP_BUTTON_CLICKED
} from '@nw/auth-core';
import { TextField } from '@nw/omega-design-system';
import { events, states } from '@nw/shared/config';
import {
  Loader,
  Relevant,
  useAutoReadSMS,
  VerifiedTickIcon
} from '@nw/shared/ui';
import { digitsPattern, isValidSixDigitOTP } from '@nw/shared/utils';

import { InputModeTypes } from '../InformedTextField/InformedTextField';
import { FieldContainer } from '../InformedTextField/styledComponents';

import {
  containerClassName,
  EmailFieldConatiner,
  ErrorIcon,
  OtpTimer,
  OTPVerifiedTag,
  OTPVerifiedText,
  ResendButton,
  ResendButtonText,
  ResendOTPWithOTPTimerContainer
} from './styledComponent';

interface OTPFieldPropTypes {
  field: string;
  isDisabled: boolean;
  label: string;
  fieldType?: string;
  shouldShowIcon: boolean;
  userFieldsData?: any;
  validationSchemaObject: any;
  touched: boolean;
  error: string;
  placeholder?: string;
  inputmode?: string;
  helperImageUrl?: string;
  inputTagStyles?: any;
  autoComplete?: string;
  shouldShowOTPField?: string;
  registrationFormState: any;
  verifyOTPButtonStyles?: any;
  isVerifyingOtp?: boolean;
  showErrorIcon?: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  labelStyles?: string;
  fieldWrapperStyles?: string;
  shouldShowLabel?: boolean;
  onSuccessOtpVerified?: () => void;
  isOtpAutofillEnabled?: boolean;
}

const InformedOTPField = React.memo((props: OTPFieldPropTypes) => {
  const { t } = useTranslation();
  const [isTimeOut, setIsTimeOut] = useState(false);
  const timer = 30;

  const {
    fieldType,
    field,
    label,
    error,
    touched,
    shouldShowIcon,
    validationSchemaObject,
    isDisabled,
    registrationFormState,
    placeholder,
    verifyOTPButtonStyles,
    isVerifyingOtp,
    showErrorIcon,
    validateOnBlur,
    validateOnChange,
    labelStyles,
    fieldWrapperStyles,
    shouldShowLabel,
    onSuccessOtpVerified,
    isOtpAutofillEnabled = false
  } = props;
  const {
    context: { otpFieldRef, formId }
  } = registrationFormState;
  const [otpFieldState, otpFieldSend]: any = useActor(otpFieldRef);

  const {
    context: { otpStateRef }
  } = otpFieldState;
  const [otpState, otpSend]: any = useActor(otpStateRef);
  const [isFocused, setIsFocused] = useState(false);

  const getVerifyOTPFieldStatus = () => {
    if (otpFieldState.tags.has(OTP_LOADING)) return API_FETCHING;
    return API_SUCCESS;
  };

  const { fieldState, fieldApi, render, informed } = useField<string>({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: validateOnChange,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    shouldShowOTPFieldAndTimer:
      otpFieldState.context.shouldShowResendAndTimerField,
    getVerifyOTPFieldStatus: getVerifyOTPFieldStatus(),
    otpFieldState: otpFieldState
  });

  const { setTouched, setValue } = fieldApi;

  const { value } = fieldState;

  const { onChange, onBlur } = informed;

  const toggleOtpTimeout = () => {
    setIsTimeOut(!isTimeOut);
    if (otpState.matches(states.RESEND_DISABLE_STATE))
      otpSend({ type: events.RESEND_DISABLE });
    otpSend({ type: events.RESEND_ENABLE });
  };

  const verifyOTPOnSubmit = (value) => {
    otpFieldSend({ type: VERIFY_OTP_BUTTON_CLICKED, values: { otp: value } });
  };

  const autoVerifyOTP = useCallback(
    (value): void => {
      onChange(value);
      verifyOTPOnSubmit(value);
    },
    [onChange, verifyOTPOnSubmit]
  );

  useAutoReadSMS(autoVerifyOTP, formId, isOtpAutofillEnabled);

  const onClickResend = (reset, start) => () => {
    otpFieldSend({ type: RESEND_OTP_CLICKED });
    toggleOtpTimeout();
    reset();
    start();
  };

  const getOtpLoader = () => (
    <OTPVerifiedTag
      css={verifyOTPButtonStyles}
      isLoader={true}
      className={'getOtpLoaderTag'}
    >
      <OTPVerifiedText>
        <Loader height={24} width={24} />
      </OTPVerifiedText>
    </OTPVerifiedTag>
  );

  const [isOtpVerified, setIsOtpVerified] = useState(false);

  const renderVerifyOTPButton = (stop) => {
    if (
      !otpFieldState.context.shouldShowResendAndTimerField &&
      !isVerifyingOtp
    ) {
      if (!isOtpVerified) {
        setIsOtpVerified(true);
        onSuccessOtpVerified && onSuccessOtpVerified();
      }
      stop();
      return (
        <OTPVerifiedTag
          css={verifyOTPButtonStyles}
          className={'otpVerifiedTag'}
        >
          <VerifiedTickIcon />
          <OTPVerifiedText>{t('common.verified')}</OTPVerifiedText>
        </OTPVerifiedTag>
      );
    } else if (Boolean(isVerifyingOtp)) {
      if (isValidSixDigitOTP(value) && isVerifyingOtp) {
        return getOtpLoader();
      }
    } else if (
      isValidSixDigitOTP(value) &&
      getVerifyOTPFieldStatus() === API_FETCHING
    ) {
      return getOtpLoader();
    }
    return null;
  };

  const getResendOTPButton = (reset, start) => {
    return (
      <ResendButton
        id="resend-otp-for-phone-number"
        type="button"
        onClick={onClickResend(reset, start)}
        disabled={!otpState.matches(states.RESEND_ENABLED_STATE)}
      >
        <ResendButtonText
          disabled={!otpState.matches(states.RESEND_ENABLED_STATE)}
        >
          {t('common.resendOTP')}
        </ResendButtonText>
      </ResendButton>
    );
  };

  const getOTPFieldDisableStatus = () => {
    if (isDisabled) {
      return isDisabled;
    } else if (
      !otpFieldState.context.shouldShowResendAndTimerField &&
      !isVerifyingOtp
    ) {
      return true;
    }
    return isDisabled;
  };
  const handleOnFocus = (): void => {
    setIsFocused(true);
  };

  const handleOnBlur = (e) => {
    onBlur(e);
    setIsFocused(false);
  };

  const renderTextField = () => {
    return (
      <>
        <TextField
          value={value as string}
          maxLength={6}
          errorMessage={error || otpFieldState.context.errorMessage}
          inputMode={'numeric' as InputModeTypes}
          type={fieldType as InputModeTypes}
          isDisabled={getOTPFieldDisableStatus()}
          label={label}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          name={field}
          pattern={digitsPattern}
          containerClassName={containerClassName}
          autoComplete="one-time-code"
          onChange={(e) => {
            const filteredValue = e.replace(/\D/g, '');
            setValue(filteredValue);
            onChange(filteredValue);
            setTouched(true);
            if (isValidSixDigitOTP(filteredValue)) {
              return verifyOTPOnSubmit(filteredValue);
            }
          }}
          placeholder={placeholder}
          labelClassName={labelStyles}
          inputClassName={'informedInputField'}
          inputContainerClassName={'informedInputContainerClassName'}
        />
        {showErrorIcon && otpFieldState.context.errorMessage && !isFocused && (
          <ErrorIcon />
        )}
      </>
    );
  };

  return (
    <FieldContainer
      key={field}
      isHidden={false}
      css={fieldWrapperStyles}
      isMarginBottomZero={true}
    >
      <Relevant when={() => otpFieldRef !== null}>
        <Timer
          initialTime={timer * 1000}
          direction="backward"
          formatValue={(value) => `${value < 10 ? `0${value}` : value}`}
          checkpoints={[
            {
              time: 0,
              callback: () => toggleOtpTimeout()
            }
          ]}
        >
          {({ reset, start, stop }) => {
            return (
              <EmailFieldConatiner>
                {renderTextField()}
                {renderVerifyOTPButton(stop)}
                {otpFieldState.context.shouldShowResendAndTimerField ? (
                  <ResendOTPWithOTPTimerContainer
                    className={'resendOtpWithTimerContainer'}
                  >
                    {getResendOTPButton(reset, start)}
                    {!isTimeOut && (
                      <OtpTimer>
                        <Timer.Seconds /> {t('common.seconds')}
                      </OtpTimer>
                    )}
                  </ResendOTPWithOTPTimerContainer>
                ) : null}
              </EmailFieldConatiner>
            );
          }}
        </Timer>
      </Relevant>
    </FieldContainer>
  );
});

InformedOTPField.displayName = 'InformedOTPField';

export { InformedOTPField };
